import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft } from 'lucide-react'
import { Button, DataTable, EmptyState, PageHeaderBand } from '@/components/ui'
import { useVendorPriceChangeFlags } from '../api/queries'
import type { FlagStatus, VendorPriceChangeFlag } from '../api/types'

/** Resolved / dismissed flags — read-only. */
export function VendorPriceChangesHistoryPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<FlagStatus>('resolved')

  const { data, isLoading, isError } = useVendorPriceChangeFlags({ status, sort: 'detected_at' })
  const flags = useMemo<VendorPriceChangeFlag[]>(() => data?.flags ?? [], [data])

  const columns = useMemo<ColumnDef<VendorPriceChangeFlag, unknown>[]>(
    () => [
      { id: 'vendor_name', header: 'Vendor', accessorKey: 'vendor_name' },
      { id: 'product_name', header: 'Product', accessorKey: 'product_name' },
      { id: 'tier_key', header: 'Tier', accessorKey: 'tier_key' },
      {
        id: 'pct_change',
        header: '% change',
        accessorKey: 'pct_change',
        cell: (ctx) => `${(ctx.getValue() as number) > 0 ? '+' : ''}${(ctx.getValue() as number).toFixed(1)}%`,
      },
      { id: 'resolved_by', header: 'Resolved by', accessorKey: 'resolved_by' },
      {
        id: 'resolved_at',
        header: 'Resolved',
        accessorKey: 'resolved_at',
        cell: (ctx) => (ctx.getValue() ? new Date(ctx.getValue() as string).toLocaleDateString() : '—'),
      },
    ],
    [],
  )

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand title="Vendor Price Changes — History" description="Resolved and dismissed flags.">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/vendor-price-changes' })}>
          <ArrowLeft size={12} /> Back to queue
        </Button>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {(['resolved', 'dismissed'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
              className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
            >
              {s === 'resolved' ? 'Resolved' : 'Dismissed'}
            </button>
          ))}
        </div>
      </PageHeaderBand>

      <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
        {isError ? (
          <EmptyState variant="error" title="Couldn't load history" description="Try refreshing." />
        ) : (
          <DataTable
            data={flags}
            columns={columns}
            loading={isLoading}
            getRowId={(r) => String(r.id)}
            onRowClick={(r) =>
              navigate({ to: '/vendor-price-changes/$flagId', params: { flagId: String(r.id) } })
            }
            moduleId="vendor-price-changes-history"
            maxHeight="100%"
            minTableWidth={1000}
            emptyState={<EmptyState variant="no-data" title="No history yet" />}
          />
        )}
      </div>
    </div>
  )
}
